<template>
  <div class="smart-breadcrumb" v-if="breadcrumbItems.length > 0">
    <a-breadcrumb separator=">" class="mb-0">
      <a-breadcrumb-item v-for="(item, index) in breadcrumbItems.slice(0, -1)" :key="item.path">
        <router-link
          v-if="index < breadcrumbItems.length - 1 && item.clickable"
          :to="item.path"
          class="breadcrumb-link"
        >
          {{ item.title }}
        </router-link>
        <span v-else class="breadcrumb-current">
          {{ item.title }}
        </span>
      </a-breadcrumb-item>
    </a-breadcrumb>
    <div class="py-0 font-bold uppercase">
      {{ breadcrumbItems[breadcrumbItems.length - 1].title }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

interface SmartBreadcrumbItem {
  path: string
  title: string
  name?: string
  clickable?: boolean
  level: number
}

const route = useRoute()
const breadcrumbItems = ref<SmartBreadcrumbItem[]>([])

// Mapping cấu trúc route parents để xây dựng breadcrumb đúng
const routeHierarchy: Record<string, { parent?: string; title?: string }> = {
  // Project Management hierarchy
  '/pm': { title: 'Thông tin dự án' },
  '/pm/project': { parent: '/pm', title: 'Danh mục dự án' },
  '/pm/project/create': { parent: '/pm/project', title: 'Tạo dự án mới' },
  '/pm/appraisal': { parent: '/pm', title: 'Thẩm định hồ sơ' },
  '/pm/appraisal/create': { parent: '/pm/appraisal', title: 'Tạo mới yêu cầu thẩm định hồ sơ' },

  // Contractor Management hierarchy
  '/cm': { title: 'Quản lý nhà thầu' },
  '/cm/contractor': { parent: '/cm', title: 'Danh sách nhà thầu' },

  // Task Management hierarchy
  '/tm': { title: 'Quản lý công việc' },

  // Settings hierarchy
  '/settings': { title: 'Cài đặt hệ thống' },
}

// Hàm tạo title từ route
const generateTitle = (routeItem: any): string => {
  if (routeItem.meta?.title) {
    return routeItem.meta.title
  }

  if (routeItem.name) {
    return routeItem.name
      .toString()
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str: string) => str.toUpperCase())
      .trim()
  }

  return routeItem.path.split('/').filter(Boolean).pop() || 'Trang chủ'
}

// Hàm xây dựng breadcrumb hierarchy từ path hiện tại
const buildBreadcrumbHierarchy = (currentPath: string): SmartBreadcrumbItem[] => {
  const items: SmartBreadcrumbItem[] = []

  // Luôn bắt đầu với trang chủ
  if (currentPath !== '/' && currentPath !== '/welcome') {
    items.push({
      path: '/',
      title: 'Trang chủ',
      clickable: true,
      level: 0,
    })
  }

  // Tìm kiếm path hierarchy
  const pathChain: string[] = []
  let currentCheckPath = currentPath

  // Xây dựng chain từ con lên cha
  while (currentCheckPath && currentCheckPath !== '/') {
    pathChain.unshift(currentCheckPath)

    // Kiểm tra trong routeHierarchy
    const hierarchyInfo = routeHierarchy[currentCheckPath]
    if (hierarchyInfo?.parent) {
      currentCheckPath = hierarchyInfo.parent
    } else {
      // Nếu không có trong hierarchy, tự động tìm parent path
      const pathParts = currentCheckPath.split('/').filter(Boolean)
      if (pathParts.length > 1) {
        pathParts.pop()
        currentCheckPath = '/' + pathParts.join('/')
      } else {
        break
      }
    }
  }

  // Thêm các items từ chain đã xây dựng
  pathChain.forEach((path, index) => {
    let title: string
    let clickable = true

    // Tìm title từ routeHierarchy trước
    if (routeHierarchy[path]?.title) {
      title = routeHierarchy[path].title!
    } else {
      // Tìm từ matched routes
      // tìm route.matched có path khớp, có trường hợp matched có path là /pm/project/:id
      const matchedRoute = route.matched.find(matched => {
        // Xử lý trường hợp dynamic route
        const matchedPathParts = matched.path.split('/').filter(Boolean)
        const currentPathParts = path.split('/').filter(Boolean)
        if (matchedPathParts.length !== currentPathParts.length) {
          return false
        }
        return matchedPathParts.every((part, idx) => {
          return part.startsWith(':') || part === currentPathParts[idx]
        })
      })

      if (matchedRoute) {
        title = generateTitle(matchedRoute)
      } else {
        // Fallback: tạo title từ path => Loại bỏ khỏi path chain
        return
        /*const pathParts = path.split('/').filter(Boolean)
        title = pathParts[pathParts.length - 1] || 'Trang'
        title = title.charAt(0).toUpperCase() + title.slice(1)*/
      }
    }

    // Item cuối cùng không clickable
    if (path === currentPath) {
      clickable = false
    }

    items.push({
      path,
      title,
      clickable,
      level: index + 1,
    })
  })

  return items
}

// Hàm xử lý dynamic routes với params
const processDynamicRoute = (path: string): SmartBreadcrumbItem[] => {
  const items = buildBreadcrumbHierarchy(path)

  // Xử lý dynamic params (như :id)
  items.forEach(item => {
    if (item.path.includes(':')) {
      // Thay thế params bằng giá trị thực
      let realPath = item.path
      Object.keys(route.params).forEach(paramKey => {
        realPath = realPath.replace(`:${paramKey}`, route.params[paramKey] as string)
      })
      item.path = realPath

      // Cập nhật title cho dynamic routes
      if (item.path === route.path && route.meta?.title) {
        item.title = route.meta.title
      }
    }
  })

  return items
}

// Hàm chính tạo breadcrumb
const generateSmartBreadcrumb = () => {
  const currentPath = route.path

  // Bỏ qua các trang đặc biệt
  const excludePaths = ['/login', '/401', '/403', '/404', '/welcome']
  if (excludePaths.includes(currentPath)) {
    breadcrumbItems.value = []
    return
  }

  // Xây dựng breadcrumb với logic thông minh
  breadcrumbItems.value = processDynamicRoute(currentPath)
}

// Watch route changes
watch(
  () => route.fullPath,
  () => {
    generateSmartBreadcrumb()
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.smart-breadcrumb {
  padding: 0 0 12px;

  .breadcrumb-link {
    color: #666;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      background: transparent;
      color: var(--color-primary);
    }
  }

  .breadcrumb-current {
    color: #333;
    font-weight: 500;
  }

  :deep(.ant-breadcrumb) {
    font-size: 14px;

    .ant-breadcrumb-separator {
      color: #ccc;
      margin: 0 8px;
    }
  }
}
</style>
